/**
 * Half-open date-range primitives shared across the app.
 *
 * The customer-team domain (and any future single-temporal domain) uses
 * `[effective_from, effective_to)` semantics — a row covers date D iff
 * `effective_from <= D < effective_to`, with `effective_to === null`
 * meaning "open ended" (still active). Two implementations of this
 * predicate (one for SQL, one for TS) drift; this module is the single
 * source of truth for the TS side. The matching SQL form is documented
 * here too so backend reviewers can verify the two agree:
 *
 *   effective_from <= date AND (effective_to IS NULL OR effective_to > date)
 *
 * Dates throughout are ISO YYYY-MM-DD strings. They compare correctly
 * via lexicographic `<`/`<=`/`>`/`>=` because zero-padded ISO sorts
 * chronologically. Don't slip non-ISO formats in here.
 */

export interface HalfOpenDateRange {
  /** Inclusive lower bound. ISO YYYY-MM-DD. */
  effectiveFrom: string;
  /** Exclusive upper bound. ISO YYYY-MM-DD, or null for open-ended. */
  effectiveTo: string | null;
}

/**
 * True iff `date` falls inside `range`. Half-open: a range ending on D
 * does NOT cover D.
 *
 *   coversDate({ from: '2026-05-11', to: '2026-05-15' }, '2026-05-14') // true
 *   coversDate({ from: '2026-05-11', to: '2026-05-15' }, '2026-05-15') // false (half-open)
 *   coversDate({ from: '2026-05-11', to: null         }, '2099-01-01') // true (open-ended)
 *   coversDate({ from: '2026-05-11', to: '2026-05-15' }, '2026-05-10') // false (before)
 */
export function coversDate(range: HalfOpenDateRange, date: string): boolean {
  if (range.effectiveFrom > date) return false;
  return range.effectiveTo == null || range.effectiveTo > date;
}

/**
 * True iff `range` overlaps the inclusive window `[windowFrom, windowTo]`.
 *
 * Used by "memberships effective during this week" queries. Note the
 * window is INCLUSIVE on both ends (timesheet ranges are date-by-date),
 * while the membership range itself is half-open.
 *
 *   from <= windowTo AND (to IS NULL OR to > windowFrom)
 */
export function overlapsWindow(
  range: HalfOpenDateRange,
  windowFrom: string,
  windowTo: string,
): boolean {
  if (range.effectiveFrom > windowTo) return false;
  return range.effectiveTo == null || range.effectiveTo > windowFrom;
}

/**
 * Customer-team membership shape this module needs to find a row covering
 * `(customerId, date)`. Kept minimal so any caller can use it — the FE's
 * `CustomerTeamMember` client type and the BE's `CustomerTeamMemberRow`
 * are both structural matches.
 */
export interface CustomerScopedHalfOpenRange extends HalfOpenDateRange {
  customerId: string;
}

/**
 * Content-derived hash of a membership list. Stable when the relevant
 * fields (id, serviceRoleId, effectiveFrom, effectiveTo) are unchanged,
 * regardless of array reference identity.
 *
 * Used by FE memos that want to survive React Query refetches (which
 * produce a new array reference per response even when content is
 * byte-identical). Keying a memo on this string instead of the array
 * reference keeps cache lines warm across the 60 s refetch / focus /
 * reconnect cadence.
 *
 * Fields included: `id` (the DB primary key — uniquely identifies a
 * row and transitively pins customerId/userId since they're not
 * patchable), `serviceRoleId` (the gate role), `effectiveFrom` and
 * `effectiveTo` (the half-open window that drives `coversDate`). All
 * fields use only `-` (UUID/ISO date), so the `:` and `|` separators
 * are collision-free.
 *
 * `.sort()` ensures the same membership *set* hashes identically
 * regardless of array order — a benign API ordering change won't
 * bust downstream caches.
 */
export function membershipsContentKey(
  memberships: readonly {
    id: string;
    serviceRoleId: string;
    effectiveFrom: string;
    effectiveTo: string | null;
  }[],
): string {
  return memberships
    .map((m) => `${m.id}:${m.serviceRoleId}:${m.effectiveFrom}:${m.effectiveTo ?? ''}`)
    .sort()
    .join('|');
}

/**
 * Find the membership row covering `(customerId, date)` in a flat array.
 *
 * Pure TS helper used by three FE pickers (timesheet week grid, day list,
 * timer popover) that all need the per-customer service role for a given
 * day. Extracted so a future tweak to the half-open semantics (or to the
 * preference between overlapping rows, should the exclusion constraint
 * ever lapse) updates ONE place instead of three.
 *
 * Returns the first row whose `customerId === customerId` AND
 * `coversDate(row, date)`. In steady-state the customer-team exclusion
 * constraint guarantees at most one match, so order doesn't matter; if
 * the constraint is ever absent the FE matches whatever the API returned
 * first — match the backend tie-breaker (`effective_from DESC`) at the
 * `listForUser` ordering layer if you want full server/client agreement.
 */
export function findCoveringMembership<T extends CustomerScopedHalfOpenRange>(
  memberships: readonly T[],
  customerId: string,
  date: string,
): T | null {
  for (const m of memberships) {
    if (m.customerId === customerId && coversDate(m, date)) return m;
  }
  return null;
}

/**
 * ALL membership rows covering `(customerId, date)` — one per service role
 * the user serves on that customer that day. Multi-role: a user may hold
 * several at once, so the timesheet pickers union their roles to scope the
 * activity catalog (the chosen activity then narrows back to a single role,
 * with a prompt only when an activity is loggable under more than one).
 */
export function findCoveringMemberships<T extends CustomerScopedHalfOpenRange>(
  memberships: readonly T[],
  customerId: string,
  date: string,
): T[] {
  return memberships.filter((m) => m.customerId === customerId && coversDate(m, date));
}

/**
 * The UNION of `serviceRoleId`s covering ANY date in `dates`, across the given
 * memberships (which the caller has already scoped to one customer). Multi-
 * role: a user may serve several roles on a customer at once, and a role may
 * become effective partway through the week — so every covering membership's
 * role counts. Used by the timesheet WEEK grid to scope the activity picker to
 * the union of a user's roles for the visible week.
 *
 * Must add EVERY covering role per day — a first-match/short-circuit would
 * silently drop a user's additional simultaneous roles.
 */
export function unionRolesCoveringDates<T extends HalfOpenDateRange & { serviceRoleId: string }>(
  memberships: readonly T[],
  dates: readonly string[],
): string[] {
  const roleIds = new Set<string>();
  for (const date of dates) {
    for (const m of memberships) {
      if (coversDate(m, date)) roleIds.add(m.serviceRoleId);
    }
  }
  return [...roleIds];
}
